import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ChartBar, ArrowLeft, CalendarBlank, Thermometer, Package, Users, CurrencyCircleDollar,
  Sun, Cloud, CloudRain, Snowflake, Warning,
} from "@phosphor-icons/react";

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Open-Meteo weathercode → ikon + label
function weatherView(code) {
  if (code === null || code === undefined) return { icon: Cloud, label: "-", color: "text-slate-400" };
  if (code === 0) return { icon: Sun, label: "Açık", color: "text-yellow-500" };
  if (code >= 1 && code <= 3) return { icon: Cloud, label: "Parçalı", color: "text-slate-500" };
  if (code >= 45 && code <= 48) return { icon: Cloud, label: "Sisli", color: "text-slate-400" };
  if (code >= 51 && code <= 67) return { icon: CloudRain, label: "Yağmurlu", color: "text-blue-500" };
  if (code >= 71 && code <= 77) return { icon: Snowflake, label: "Karlı", color: "text-sky-400" };
  if (code >= 80 && code <= 82) return { icon: CloudRain, label: "Sağanak", color: "text-blue-600" };
  if (code >= 95) return { icon: CloudRain, label: "Fırtınalı", color: "text-purple-500" };
  return { icon: Cloud, label: "-", color: "text-slate-400" };
}

export default function DailyReport() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();  // eslint-disable-line
  const [date, setDate] = useState(todayISO());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/sites/${siteId}/daily-report`, { params: { date } });
      setReport(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [siteId, date]);

  const w = report?.weather;
  const wv = weatherView(w?.weathercode);
  const WIcon = wv.icon;

  const printPage = () => window.print();

  return (
    <Shell
      title={report ? `${report.site_name} — Gün Sonu Raporu` : "Yükleniyor…"}
      subtitle="Proje İlerleme › Gün Sonu"
      hideSiteSelector
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/santiyeler`)}
                  className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">
            <ArrowLeft size={14} className="mr-1.5" /> Şantiyeler
          </Button>
          <Button onClick={printPage} data-testid="report-print"
                  className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-[#1B4776] hover:bg-[#153859] text-white">
            Yazdır / PDF
          </Button>
        </div>
      }
    >
      {/* Tarih Seçici */}
      <Card className="p-4 rounded-none sm:rounded-sm mb-4 flex items-center gap-4">
        <CalendarBlank size={20} weight="duotone" className="text-[#95C11F]" />
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Rapor Tarihi:</span>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)}
                 data-testid="report-date"
                 className="w-44 h-9 rounded-none sm:rounded-sm font-mono" />
        </div>
        <div className="text-sm text-slate-500">
          {new Date(date + "T00:00:00").toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-16 text-sm text-slate-500">Yükleniyor…</div>
      ) : !report ? (
        <div className="text-center py-16 text-sm text-slate-500">Veri yok</div>
      ) : (
        <>
          {/* Üst özet — 4 kart */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="report-summary">
            <Card className="p-4 rounded-none sm:rounded-sm border-l-4 border-[#95C11F]">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-1">
                <ChartBar size={11} /> İmalat Girişi
              </div>
              <div className="text-3xl font-bold text-[#1B4776] mt-1 font-heading">
                {report.production.entries_count}
              </div>
              <div className="text-xs text-slate-500">satır</div>
            </Card>
            <Card className="p-4 rounded-none sm:rounded-sm">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-1">
                <CurrencyCircleDollar size={11} /> Üretilen Değer
              </div>
              <div className="text-2xl font-bold text-[#95C11F] mt-1 font-mono">
                ₺{(report.production.total_value || 0).toLocaleString("tr-TR", { maximumFractionDigits: 0 })}
              </div>
            </Card>
            <Card className="p-4 rounded-none sm:rounded-sm">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-1">
                <Package size={11} /> Sarfiyat Kalemi
              </div>
              <div className="text-3xl font-bold text-amber-600 mt-1 font-heading">
                {(report.consumption || []).length}
              </div>
            </Card>
            <Card className="p-4 rounded-none sm:rounded-sm">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-1">
                <Thermometer size={11} /> Hava
              </div>
              {w && !w.error ? (
                <div className="mt-1 flex items-center gap-2">
                  <WIcon size={28} weight="duotone" className={wv.color} />
                  <div>
                    <div className="font-bold text-slate-900 font-mono text-lg">
                      {w.temp_max != null ? `${Math.round(w.temp_max)}°` : "-"}
                      <span className="text-slate-400 text-sm"> / {w.temp_min != null ? `${Math.round(w.temp_min)}°` : "-"}</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{wv.label}</div>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-400 italic mt-2">
                  {w?.error ? "servis yanıt vermedi" : "koordinat çözülemedi"}
                </div>
              )}
            </Card>
          </div>

          {/* İmalat Detay */}
          <Card className="rounded-none sm:rounded-sm mb-4">
            <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
              <ChartBar size={16} weight="duotone" className="text-[#95C11F]" />
              <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700">Yapılan İmalatlar</span>
              <span className="text-xs text-slate-500">({report.production.rows.length})</span>
            </div>
            {report.production.rows.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500 italic" data-testid="report-no-production">
                Bu gün için imalat girişi yok.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-3 py-2">Etap</th>
                      <th className="text-left px-3 py-2">Blok</th>
                      <th className="text-left px-3 py-2">İmalat</th>
                      <th className="text-right px-3 py-2">Miktar</th>
                      <th className="text-right px-3 py-2">B.Fiyat</th>
                      <th className="text-right px-3 py-2">Tutar</th>
                      <th className="text-left px-3 py-2">Girişi Yapan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.production.rows.map((r, i) => (
                      <tr key={i} data-testid={`report-prod-row-${i}`}
                          className="border-b border-slate-100">
                        <td className="px-3 py-1.5 text-xs text-slate-600">{r.phase_name || "-"}</td>
                        <td className="px-3 py-1.5 text-xs font-medium">
                          {r.block_name} <span className="text-slate-400 font-mono">{r.block_code}</span>
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="text-xs font-medium">{r.wbs_name}</div>
                          <div className="text-[10px] text-slate-400 font-mono">{r.wbs_code}</div>
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-xs">
                          {r.qty.toLocaleString("tr-TR")} <span className="text-slate-400">{r.unit}</span>
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-xs">
                          {r.unit_price > 0 ? `₺${r.unit_price.toLocaleString("tr-TR")}` : "-"}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-xs font-bold text-[#1B4776]">
                          {r.value > 0 ? `₺${r.value.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}` : "-"}
                        </td>
                        <td className="px-3 py-1.5 text-xs text-slate-500">{r.user_name || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Sarfiyat */}
          <Card className="rounded-none sm:rounded-sm mb-4">
            <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
              <Package size={16} weight="duotone" className="text-amber-600" />
              <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700">Tahmini Sarfiyat</span>
              <span className="text-xs text-slate-500">(WBS normlarından hesaplandı)</span>
            </div>
            {(report.consumption || []).length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500 italic" data-testid="report-no-consumption">
                Sarfiyat hesabı için WBS kalemlerine <b>norm</b> tanımlanmış olmalı.
              </div>
            ) : (
              <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                {report.consumption.map((c, i) => (
                  <div key={i} data-testid={`report-consum-${i}`}
                       className="border border-slate-200 p-3 flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-sm text-slate-900 truncate">{c.material_name}</div>
                    </div>
                    <div className="text-right font-mono">
                      <div className="text-lg font-bold text-amber-700">
                        {c.qty.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">{c.unit}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Personel özeti (varsa) */}
          {report.personnel && (
            <Card className="rounded-none sm:rounded-sm mb-4">
              <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
                <Users size={16} weight="duotone" className="text-[#1B4776]" />
                <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700">Personel Puantajı</span>
              </div>
              <div className="p-4 flex gap-6">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Toplam Adam-Gün</div>
                  <div className="text-2xl font-bold text-[#1B4776] font-heading">
                    {report.personnel.total_man_days.toFixed(1)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Personel Sayısı</div>
                  <div className="text-2xl font-bold text-[#95C11F] font-heading">
                    {report.personnel.unique_crew}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Hava detay */}
          {w && !w.error && (
            <Card className="rounded-none sm:rounded-sm mb-4">
              <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
                <Thermometer size={16} weight="duotone" className="text-red-500" />
                <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700">Hava Durumu</span>
                <span className="text-xs text-slate-500">
                  ({w.location_source === "geocode" ? `otomatik: ${w.resolved_name || "-"}` : "manuel koordinat"})
                </span>
              </div>
              <div className="p-4 grid grid-cols-4 gap-4">
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Max</div>
                     <div className="text-2xl font-bold text-red-600 font-mono">{w.temp_max != null ? `${w.temp_max.toFixed(1)}°C` : "-"}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Ortalama</div>
                     <div className="text-2xl font-bold text-slate-700 font-mono">{w.temp_mean != null ? `${w.temp_mean.toFixed(1)}°C` : "-"}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Min</div>
                     <div className="text-2xl font-bold text-blue-600 font-mono">{w.temp_min != null ? `${w.temp_min.toFixed(1)}°C` : "-"}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Koşul</div>
                     <div className="text-lg font-bold mt-1 flex items-center gap-1">
                       <WIcon size={22} weight="duotone" className={wv.color} /> {wv.label}
                     </div></div>
              </div>
            </Card>
          )}
        </>
      )}
    </Shell>
  );
}
